import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, ClockIcon, MapPinIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { LOCATIONS } from '../data/locations';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/81863cf8-fe5b-47f4-8510-439876f3cde4.jpg';
function toIntlNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? `+61${digits.slice(1)}` : digits;
}
export function Contact() {
  const [slug, setSlug] = useState(LOCATIONS[0].slug);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);
  const location = LOCATIONS.find(l => l.slug === slug) ?? LOCATIONS[0];
  const message = `Hi ${location.name}, ${body || '[your question]'}${name ? ` — ${name}` : ''}`;
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    window.location.href = `sms:${toIntlNumber(location.phone)}?&body=${encodeURIComponent(message)}`;
    setSent(true);
  }
  return <main className="w-full bg-black" data-id="element-177" data-mp="19enwyz9ri315">
      <PageHero image={HERO_IMAGE} eyebrow="Say Hello" title="Contact Us" subtitle="Reach the studio directly. We're here to answer anything you'd like to ask." data-id="element-178" data-mp="1e7h0jjthv5bn" />

      <section aria-label="Contact details" className="w-full bg-black" data-id="element-179" data-mp="mhcm01sf1cic">
        <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2" data-id="element-180" data-mp="qhwh6ztfi61u">
          <div data-id="element-181" data-mp="vh96dr7rvp7l">
            <h2 className="text-3xl font-bold tracking-tight text-white" data-id="element-182" data-mp="vgpvuvkgc33l">Send us a message</h2>
            <p className="mt-3 text-neutral-400" data-id="element-183" data-mp="10gvghrsb0pc6">
              Pick a venue and we'll open a pre-filled text message to that shop.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-id="element-184" data-mp="26w66bw5hvytq">
              <div data-id="element-185" data-mp="2bug32b6vb7hg">
                <label htmlFor="location" className="block text-sm font-semibold text-white" data-id="element-186" data-mp="2btxx2muelewc">
                  Venue
                </label>
                <select id="location" name="location" value={slug} onChange={e => {
                setSlug(e.target.value);
                setSent(false);
              }} className="mt-2 w-full rounded-lg border border-white/10 bg-ink-850 px-4 py-3.5 text-white focus:border-lotus-500 focus:outline-none" data-id="element-187" data-mp="1h8ivmjo344zh">
                  {LOCATIONS.map(loc => <option key={loc.slug} value={loc.slug} data-id="element-188" data-mp="yfog9omjuauq">
                      {loc.suburb} — {loc.name}
                    </option>)}
                </select>
              </div>

              <div data-id="element-189" data-mp="18dvw2453qmv5">
                <label htmlFor="name" className="block text-sm font-semibold text-white" data-id="element-190" data-mp="kq4hetw39xqp">
                  Name
                </label>
                <input id="name" name="name" value={name} onChange={e => setName(e.target.value)} required placeholder="Your name" className="mt-2 w-full rounded-lg border border-white/10 bg-ink-850 px-4 py-3.5 text-white placeholder:text-neutral-500 focus:border-lotus-500 focus:outline-none" data-id="element-191" data-mp="fq89ijk9tm5v" />
              </div>

              <div data-id="element-192" data-mp="3a5e3cr6lv50j">
                <label htmlFor="message" className="block text-sm font-semibold text-white" data-id="element-193" data-mp="3a4uvy4ekzz0r">
                  Message
                </label>
                <textarea id="message" name="message" rows={4} value={body} onChange={e => setBody(e.target.value)} required placeholder="How can we help?" className="mt-2 w-full resize-y rounded-lg border border-white/10 bg-ink-850 px-4 py-3.5 text-white placeholder:text-neutral-500 focus:border-lotus-500 focus:outline-none" data-id="element-194" data-mp="3f3fvs2px8goo" />
              </div>

              <div className="rounded-xl border border-white/5 bg-ink-850 p-5" data-id="element-195" data-mp="2a03s98a8c32u">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-196" data-mp="255zn8ukiy54e">
                  Message preview
                </p>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-197" data-mp="1v9muq00ptxb8">{message}</p>
                <p className="mt-3 text-xs text-neutral-500" data-id="element-198" data-mp="2swmynptnfcvp">
                  Sends to {location.name} on {location.phone}
                </p>
              </div>

              <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-lotus-500 px-8 py-4 text-base font-semibold text-black transition-colors hover:bg-lotus-600" data-id="element-199" data-mp="2nylhxbjp3pne">
                <MessageSquareIcon className="h-5 w-5" aria-hidden="true" data-id="element-200" data-mp="17jshhhchi6qg" />
                Text this venue
              </button>

              {sent && <p role="status" className="flex items-center justify-center gap-2 text-sm text-lotus-400" data-id="element-201" data-mp="13kbb8lp8h5k8">
                  <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-202" data-mp="3j7oxj3i0menq" />
                  Your messaging app should now be open — press send.
                </p>}
            </form>
          </div>

          <div data-id="element-203" data-mp="3sy0653cn9c0y">
            <h2 className="text-3xl font-bold tracking-tight text-white" data-id="element-204" data-mp="3syjogl67i2fi">Our Locations</h2>
            <ul className="mt-8 space-y-5" data-id="element-205" data-mp="1si6rtrqkakw">
              {LOCATIONS.map(loc => <li key={loc.slug} className="rounded-2xl border border-white/5 bg-ink-850 p-6" data-id="element-206" data-mp="6nnt2tp1vr9i">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-lotus-500" data-id="element-207" data-mp="arvjcvb5pf9u">
                    {loc.suburb}
                  </p>
                  <h3 className="mt-2 text-lg font-bold text-white" data-id="element-208" data-mp="2ciyudloykvux">
                    <Link to={`/locations/${loc.slug}`} className="hover:text-lotus-500" data-id="element-209" data-mp="2cktit9zfv63r">
                      {loc.name}
                    </Link>
                  </h3>
                  <p className="mt-4 flex items-start gap-3 text-sm text-neutral-300" data-id="element-210" data-mp="3kszb36enz5z6">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-211" data-mp="3fuo1mwsa6xhh" />
                    {loc.address}
                  </p>
                  <p className="mt-3 flex items-start gap-3 text-sm text-neutral-300" data-id="element-212" data-mp="3ow5gycxv6v54">
                    <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-213" data-mp="7k31slndqp2z" />
                    <a href={`tel:${loc.phone.replace(/\s/g, '')}`} className="hover:text-lotus-500" data-id="element-214" data-mp="gehhuqfqi3n5">
                      {loc.phone}
                    </a>
                  </p>
                  <p className="mt-3 flex items-start gap-3 text-sm text-neutral-300" data-id="element-215" data-mp="qab26vzp31fh">
                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-500" aria-hidden="true" data-id="element-216" data-mp="lf5dxqitlao8" />
                    {loc.hours}
                  </p>
                </li>)}
            </ul>
          </div>
        </div>
      </section>
    </main>;
}